import { responsiveFontSizes } from '@mui/material';
import { ThemeOptions, createTheme as createMuiTheme } from '@mui/material/styles';
import { darken, lighten } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import colors from './colors';
import { components } from './overrides';
import { shadows } from './shadows';
import typography from './typography';

export const THEMES = {
  LIGHT: 'LIGHT',
  DARK: 'DARK',
  HMI: 'HMI', // this theme is defined in hmi/front-end/src/styles/theme.ts
};

const baseOptions: ThemeOptions = {};

const themesOptions = [
  {
    name: THEMES.LIGHT,
    palette: {
      primary: {
        light: '#85B596',
        main: colors.brand.main,
        dark: '#3d644b',
        background: '#f4f9f6',
        contrastText: '#fff',
      },
      secondary: {
        light: lighten(colors.grey[800], 0.2),
        main: colors.grey[800],
        dark: darken(colors.grey[800], 0.25),
        background: lighten(colors.grey[800], 0.94),
        contrastText: '#fff',
      },
      text: {
        primary: colors.grey[1000],
        secondary: colors.grey[800],
        error: '#A40707',
        warning: '#6d4900',
        success: '#024f1f',
        info: '#044e75',
      },
      background: {
        default: '#fafafa',
        grey: '#ECECEC',
        lightGrey: colors.grey[50],
      },
      action: {
        disabled: colors.grey[500], // used by buttons and some non MUI inputs
        disabledBackground: colors.grey[300],
        hover: lighten(colors.brand.main, 0.95),
      },
      error: {
        light: lighten(colors.state.error, 0.15),
        main: colors.state.error,
        dark: '#d40a0a',
        background: '#fef0f0',
        contrastText: '#fff',
      },
      warning: {
        light: lighten(colors.state.warning, 0.15),
        main: colors.state.warning,
        dark: '#ffab07',
        background: '#fff9ef',
        contrastText: colors.grey[900],
      },
      success: {
        light: lighten(colors.state.success, 0.15),
        main: colors.state.success,
        dark: '#04993c',
        background: '#ebf8f0',
        contrastText: '#fff',
      },
      info: {
        light: lighten(colors.state.info, 0.15),
        main: colors.state.info,
        dark: '#067fbf',
        background: '#e5f6fd',
        contrastText: '#fff',
      },
      accent: {
        light: colors.grey[800],
        main: colors.grey[1000],
        dark: '#1e1e1e',
        contrastText: '#fff',
      },
      white: '#FFFFFF',
      grey: colors.grey,
      ...colors.badge,
      brand: colors.brand,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
  },
  {
    name: THEMES.DARK,
    // Soft, low-glare dark palette tuned for night shifts: muted blue-grey
    // surfaces (never pure black) with the brand green kept as the accent.
    palette: {
      mode: 'dark',
      primary: {
        light: '#85B596',
        main: colors.brand.main,
        dark: '#3D644B',
        background: '#1F2A23',
        contrastText: '#fff',
      },
      secondary: {
        light: lighten(colors.grey[300], 0.2),
        main: colors.grey[300],
        dark: darken(colors.grey[300], 0.25),
        background: '#2A3742',
        contrastText: colors.grey[1000],
      },
      text: {
        primary: '#FFFFFF',
        secondary: colors.grey[300],
        error: '#FF8A8A',
        warning: '#FFCE6B',
        success: '#7DDFA3',
        info: '#7CC6F0',
      },
      background: {
        default: '#1A2027',
        dark: '#0E1419',
        paper: '#232E38',
        grey: '#2A3742',
        lightGrey: '#1F2730',
      },
      action: {
        active: 'rgba(255, 255, 255, 0.6)',
        hover: 'rgba(255, 255, 255, 0.08)',
        selected: 'rgba(255, 255, 255, 0.16)',
        disabled: colors.grey[600],
        disabledBackground: 'rgba(255, 255, 255, 0.12)',
        focus: 'rgba(255, 255, 255, 0.12)',
      },
      error: {
        light: lighten(colors.state.error, 0.15),
        main: colors.state.error,
        dark: '#D40A0A',
        background: '#3A1F1F',
        contrastText: '#fff',
      },
      warning: {
        light: lighten(colors.state.warning, 0.15),
        main: colors.state.warning,
        dark: '#FFAB07',
        background: '#3A3220',
        contrastText: colors.grey[1000],
      },
      success: {
        light: lighten(colors.state.success, 0.15),
        main: colors.state.success,
        dark: '#04993C',
        background: '#1F3A2A',
        contrastText: '#fff',
      },
      info: {
        light: lighten(colors.state.info, 0.15),
        main: colors.state.info,
        dark: '#067FBF',
        background: '#1F2F3A',
        contrastText: '#fff',
      },
      accent: {
        light: colors.grey[400],
        main: colors.grey[300],
        dark: colors.grey[500],
        contrastText: colors.grey[1000],
      },
      white: '#FFFFFF',
      grey: colors.grey,
      ...colors.badge,
      brand: colors.brand,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
  },
];

type ThemeConfig = {
  componentsOverride?: object;
  typographyOverride?: object;
  theme?: string;
  direction?: string;
  responsiveFontSizes?: boolean;
};

export const createTheme = (
  config: ThemeConfig = {
    componentsOverride: {},
    typographyOverride: {},
    theme: THEMES.LIGHT,
    direction: 'ltr',
    responsiveFontSizes: false,
  }
) => {
  let themeOptions = themesOptions.find((theme) => theme.name === config.theme);

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    [themeOptions] = themesOptions;
  }

  const finalThemeOptions = {
    ...themeOptions,
    components: { ...components, ...config.componentsOverride },
    typography: { ...typography, ...config.typographyOverride },
    direction: config.direction,
    shadows,
  };

  let theme = createMuiTheme(deepmerge(baseOptions, finalThemeOptions));

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};

export const getTheme = (theme = THEMES.LIGHT) => {
  const themeOptions = themesOptions.find((t) => t.name === theme);
  return themeOptions ? themeOptions : null;
};

export const getPalette = (theme = THEMES.LIGHT) => {
  return getTheme(theme)?.palette;
};

export default {
  getTheme,
  getPalette,
  THEMES,
  createTheme,
  colors,
};
